import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useKeycloak } from "@react-keycloak/web";
import { InstalledBaseSubscriptionsSla } from "../graphql/queries/installedBaseSubscriptionsSla";
import { isSlaEnabled } from "../utils/slaEnabled";
import { isIoTAdminToken } from "../utils/iotAttachGate";

export const SLA_SUBSCRIPTIONS_QUERY_NAME = "InstalledBaseSubscriptionsSla";

/**
 * SLA status change opt-in of the user's node and region subscriptions.
 * Like every SLA feature it is limited to global/region admins and the SLA
 * flag; otherwise the query is skipped and `showSla` is false.
 */
const useSlaSubscriptionSettings = () => {
    const { keycloak } = useKeycloak();
    const showSla = isSlaEnabled() && isIoTAdminToken(keycloak.token);

    const { data, loading } = useQuery(InstalledBaseSubscriptionsSla, {
        fetchPolicy: "cache-and-network",
        skip: !showSla,
    });

    // Subscription id → opt-in, per kind (node and region ids may collide).
    const slaById = useMemo(() => {
        const toMap = (subs: any[] | null | undefined) => {
            const map: Record<string, boolean> = {};
            (subs ?? []).forEach((sub) => {
                if (sub?.id) map[String(sub.id)] = !!sub.slaStatusChange;
            });
            return map;
        };
        return {
            node: toMap(data?.installedBaseNodeSubscriptionList?.subscriptions),
            region: toMap(data?.installedBaseRegionSubscriptionList?.subscriptions),
        };
    }, [data]);

    return {
        showSla,
        slaById,
        /** True until the first result is there (edits must not start from a wrong `false`). */
        slaPending: showSla && loading && !data,
        refetchQueries: showSla ? [SLA_SUBSCRIPTIONS_QUERY_NAME] : [],
    };
};

export default useSlaSubscriptionSettings;
